/**
 * Main Auction Phase Component
 * Handles role-based routing and phase-gating for auction functionality
 */
import { useState, useEffect, useCallback } from 'react';
import { axiosInstance } from '../../config/axios';
import { useCurrentUser, AUCTION_STATUS_COLORS } from './useAuction';

// Import sub-components
import AuctionConfigPanel from './AuctionConfigPanel';
import TeamManagementPanel from './TeamManagementPanel';
import PlayerPoolPanel from './PlayerPoolPanel';
import LiveAuctionConsole from './LiveAuctionConsole';
import TeamOwnerDashboard from './TeamOwnerDashboard';
import PublicAuctionView from './PublicAuctionView';
import PreAuctionSetupPanel from './PreAuctionSetupPanel';

export default function AuctionPhase({ phase, event }) {
  const { user, loading: userLoading, isAuctioneer, isTeamOwner } = useCurrentUser();
  const [config, setConfig] = useState(null);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeAdminTab, setActiveAdminTab] = useState('config');

  const fetchAuctionData = useCallback(async () => {
    try {
      setLoading(true);
      // Get auction config
      const configRes = await axiosInstance.get(`/v1/events/${event.id}/auction/config`).catch(() => ({ data: null }));
      setConfig(configRes.data);
      
      // Get teams if config exists
      if (configRes.data) {
        const teamsRes = await axiosInstance.get(`/v1/events/${event.id}/auction/teams`).catch(() => ({ data: [] }));
        setTeams(teamsRes.data);
      }
    } catch (err) {
      console.error('Failed to fetch auction data:', err);
    } finally {
      setLoading(false);
    }
  }, [event.id]);

  useEffect(() => {
    fetchAuctionData();
  }, [fetchAuctionData]);

  // Show loading state
  if (loading || userLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#3AAFA9]"></div>
      </div>
    );
  }

  // Determine which view to show based on user role
  const renderContent = () => {
    // Admin view: this event's admins (owner, co-admins, Super Admins)
    if (event?.can_manage) {
      return (
        <AdminView
          eventId={event.id}
          config={config}
          teams={teams}
          phase={phase}
          activeTab={activeAdminTab}
          onTabChange={setActiveAdminTab}
          onRefresh={fetchAuctionData}
        />
      );
    }

    // Auctioneer View (only when auction is CONFIGURED or LIVE)
    if (isAuctioneer && (config?.auction_status === 'CONFIGURED' || config?.auction_status === 'LIVE')) {
      return (
        <LiveAuctionConsole
          eventId={event.id}
          config={config}
          phase={phase}
          onStateChange={fetchAuctionData}
        />
      );
    }

    // Team Owner View
    if (isTeamOwner && config) {
      return (
        <TeamOwnerDashboard
          eventId={event.id}
          config={config}
          userId={user?.id}
        />
      );
    }

    // Public View (default)
    return (
      <PublicAuctionView
        eventId={event.id}
        config={config}
        phase={phase}
      />
    );
  };

  return (
    <div className="space-y-6" data-testid="auction-phase">
      {renderContent()}
    </div>
  );
}

// Admin View Component
function AdminView({ eventId, config, teams, phase, activeTab, onTabChange, onRefresh }) {
  const [pool, setPool] = useState([]);
  
  // Fetch pool for pre-auction setup
  useEffect(() => {
    if (config) {
      axiosInstance.get(`/v1/events/${eventId}/auction/pool`)
        .then(res => setPool(res.data))
        .catch(() => {});
    }
  }, [eventId, config]);
  
  const ADMIN_TABS = [
    { key: 'config', label: 'Configuration', icon: 'ph-gear' },
    { key: 'teams', label: 'Teams', icon: 'ph-users-three' },
    { key: 'pool', label: 'Player Pool', icon: 'ph-list-bullets' },
    { key: 'setup', label: 'Pre-Auction Setup', icon: 'ph-list-checks' },
    { key: 'live', label: 'Live Console', icon: 'ph-broadcast' },
    { key: 'squads', label: 'Team Squads', icon: 'ph-list-numbers' },
  ];

  // Check if current user is super_admin
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isSuperAdmin = user.role === 'super_admin' || (user.roles || []).includes('super_admin');

  // Only show relevant tabs based on auction status
  const availableTabs = ADMIN_TABS.filter(tab => {
    if (!config && tab.key !== 'config') return false;
    if (tab.key === 'setup') {
      if (['DRAFT', 'CONFIGURED'].includes(config?.auction_status)) return true;
      if (isSuperAdmin && ['LIVE', 'PAUSED', 'SETUP'].includes(config?.auction_status)) return true;
      return false;
    }
    if (tab.key === 'live' && !['CONFIGURED', 'LIVE', 'PAUSED'].includes(config?.auction_status)) return false;
    if (tab.key === 'squads' && !['LIVE', 'PAUSED', 'CLOSED'].includes(config?.auction_status)) return false;
    return true;
  });

  return (
    <div className="space-y-6" data-testid="admin-auction-view">
      {/* Status Overview */}
      {config && (
        <div className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-500">Status:</span>
                <span className={`px-3 py-1 rounded-full text-sm font-bold ${AUCTION_STATUS_COLORS[config.auction_status]}`}>
                  {config.auction_status}
                </span>
              </div>
              {config.locked_by_admin && (
                <span className="text-amber-600 text-sm font-medium flex items-center gap-1">
                  <i className="ph-bold ph-lock"></i> Locked
                </span>
              )}
            </div>
            <div className="flex items-center gap-4 text-sm text-gray-500">
              <span><strong className="text-[#2B3A67]">{teams.length}</strong>/{config.total_teams} Teams</span>
              <span><strong className="text-[#2B3A67]">{config.squad_size}</strong> Squad Size</span>
            </div>
          </div>
        </div>
      )}

      {/* Admin Tab Navigation */}
      <div className="flex gap-2 overflow-x-auto pb-2">
        {availableTabs.map(tab => (
          <button
            key={tab.key}
            onClick={() => onTabChange(tab.key)}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold whitespace-nowrap transition-all ${
              activeTab === tab.key
                ? 'bg-[#2B3A67] text-white shadow-lg shadow-[#2B3A67]/20'
                : 'bg-white text-gray-600 border border-gray-200 hover:border-[#2B3A67] hover:text-[#2B3A67]'
            }`}
            data-testid={`admin-tab-${tab.key}`}
          >
            <i className={`ph-bold ${tab.icon}`}></i>
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div>
        {activeTab === 'config' && (
          <AuctionConfigPanel
            eventId={eventId}
            config={config}
            teams={teams}
            onConfigChange={onRefresh}
          />
        )}
        {activeTab === 'teams' && config && (
          <TeamManagementPanel
            eventId={eventId}
            config={config}
            onTeamsChange={onRefresh}
          />
        )}
        {activeTab === 'pool' && config && (
          <PlayerPoolPanel
            eventId={eventId}
            config={config}
            teams={teams}
            onPoolChange={onRefresh}
          />
        )}
        {activeTab === 'setup' && config && (
          <PreAuctionSetupPanel
            eventId={eventId}
            config={config}
            teams={teams}
            pool={pool}
            onSetupComplete={() => {
              onTabChange('live');
              onRefresh();
            }}
          />
        )}
        {activeTab === 'live' && config && (
          <LiveAuctionConsole
            eventId={eventId}
            config={config}
            phase={phase}
            onStateChange={onRefresh}
          />
        )}
        {activeTab === 'squads' && config && (
          <AdminTeamSquadsPanel eventId={eventId} teams={teams} />
        )}
      </div>
    </div>
  );
}


// Admin Team Squads Panel — shows all teams with their bought players
function AdminTeamSquadsPanel({ eventId, teams }) {
  const [squads, setSquads] = useState({});
  const [expandedTeam, setExpandedTeam] = useState(null);

  const fetchSquad = async (teamId) => {
    if (squads[teamId]) return;
    try {
      const res = await axiosInstance.get(`/v1/events/${eventId}/auction/teams/${teamId}/squad`);
      setSquads(prev => ({ ...prev, [teamId]: res.data }));
    } catch {
      console.error('Failed to fetch squad');
    }
  };

  const handleToggle = (teamId) => {
    if (expandedTeam === teamId) {
      setExpandedTeam(null);
    } else {
      setExpandedTeam(teamId);
      fetchSquad(teamId);
    }
  };

  // Refresh squads when team data changes
  const squadKey = teams.map(t => `${t.id}:${t.squad_count}`).join(',');
  useEffect(() => { setSquads({}); }, [squadKey]);

  const sortedTeams = [...teams].sort((a, b) => (b.squad_count || 0) - (a.squad_count || 0));
  const totalPlayers = teams.reduce((sum, t) => sum + (t.squad_count || 0), 0);
  const totalSpent = teams.reduce((sum, t) => sum + (t.wallet_total_spent_byc || 0), 0);

  return (
    <div className="space-y-4">
      {/* Summary */}
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-[#2B3A67]/5 rounded-xl p-3 text-center">
          <p className="text-2xl font-extrabold text-[#2B3A67]">{teams.length}</p>
          <p className="text-xs text-gray-500">Teams</p>
        </div>
        <div className="bg-[#3AAFA9]/5 rounded-xl p-3 text-center">
          <p className="text-2xl font-extrabold text-[#3AAFA9]">{totalPlayers}</p>
          <p className="text-xs text-gray-500">Players Bought</p>
        </div>
        <div className="bg-[#E54562]/5 rounded-xl p-3 text-center">
          <p className="text-2xl font-extrabold text-[#E54562]">₿ {new Intl.NumberFormat('en-IN').format(totalSpent)}</p>
          <p className="text-xs text-gray-500">Total Spent</p>
        </div>
      </div>

      {/* Team Cards */}
      <div className="space-y-3">
        {sortedTeams.map(team => (
          <div key={team.id} className="bg-white rounded-xl border border-gray-100 overflow-hidden">
            <button
              onClick={() => handleToggle(team.id)}
              className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
              data-testid={`admin-squad-toggle-${team.id}`}
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#2B3A67] flex items-center justify-center overflow-hidden">
                  {team.team_logo_url ? (
                    <img src={team.team_logo_url} alt={team.team_name} className="w-full h-full object-cover" />
                  ) : (
                    <span className="text-white font-bold">{team.team_name[0]}</span>
                  )}
                </div>
                <div className="text-left">
                  <p className="font-bold">{team.team_name}</p>
                  <p className="text-xs text-gray-500">
                    {team.squad_count || 0} players · {team.remaining_slots ?? '?'} slots left
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="text-right">
                  <p className="font-bold text-[#3AAFA9] text-sm">₿ {new Intl.NumberFormat('en-IN').format(team.wallet_balance_byc || 0)}</p>
                  <p className="text-[10px] text-gray-400">Balance</p>
                </div>
                <i className={`ph-bold ph-caret-${expandedTeam === team.id ? 'up' : 'down'} text-gray-400`}></i>
              </div>
            </button>

            {expandedTeam === team.id && (
              <div className="border-t border-gray-100 bg-gray-50 p-4">
                {!squads[team.id] ? (
                  <div className="flex justify-center py-4">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#3AAFA9]"></div>
                  </div>
                ) : squads[team.id].length === 0 ? (
                  <p className="text-center text-gray-400 text-sm py-4">No players acquired yet</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-gray-200">
                          <th className="text-left py-2 px-2 text-xs text-gray-500 font-bold">#</th>
                          <th className="text-left py-2 px-2 text-xs text-gray-500 font-bold">Player</th>
                          <th className="text-left py-2 px-2 text-xs text-gray-500 font-bold">Role</th>
                          <th className="text-left py-2 px-2 text-xs text-gray-500 font-bold">Type</th>
                          <th className="text-right py-2 px-2 text-xs text-gray-500 font-bold">Price</th>
                        </tr>
                      </thead>
                      <tbody>
                        {squads[team.id].map((player, idx) => (
                          <tr key={player.id} className="border-b border-gray-100 last:border-0">
                            <td className="py-2 px-2 text-gray-400">{idx + 1}</td>
                            <td className="py-2 px-2">
                              <div className="flex items-center gap-2">
                                {player.player_photo_url ? (
                                  <img src={player.player_photo_url} alt="" className="w-7 h-7 rounded-full object-cover" />
                                ) : (
                                  <span className="w-7 h-7 rounded-full bg-[#2B3A67] flex items-center justify-center text-white text-xs font-bold">
                                    {player.player_name?.[0]}
                                  </span>
                                )}
                                <span className="font-medium">{player.player_name}</span>
                              </div>
                            </td>
                            <td className="py-2 px-2 text-gray-500">{player.player_category || '-'}</td>
                            <td className="py-2 px-2">
                              {player.acquisition_type === 'ICON' && (
                                <span className="bg-yellow-100 text-yellow-700 text-[10px] px-1.5 py-0.5 rounded font-bold">ICON</span>
                              )}
                              {player.acquisition_type === 'OWNER_PLAYER' && (
                                <span className="bg-purple-100 text-purple-600 text-[10px] px-1.5 py-0.5 rounded font-bold">OWNER</span>
                              )}
                              {player.acquisition_type === 'AUCTION' && (
                                <span className="bg-blue-100 text-blue-600 text-[10px] px-1.5 py-0.5 rounded font-bold">AUCTION</span>
                              )}
                              {!['ICON', 'OWNER_PLAYER', 'AUCTION'].includes(player.acquisition_type) && player.acquisition_type && (
                                <span className="bg-gray-100 text-gray-600 text-[10px] px-1.5 py-0.5 rounded font-bold">{player.acquisition_type}</span>
                              )}
                            </td>
                            <td className="py-2 px-2 text-right font-bold text-[#E54562]">₿ {new Intl.NumberFormat('en-IN').format(player.sold_price_byc || 0)}</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr className="border-t-2 border-gray-200">
                          <td colSpan="4" className="py-2 px-2 font-bold text-gray-600">Total</td>
                          <td className="py-2 px-2 text-right font-bold text-[#E54562]">
                            ₿ {new Intl.NumberFormat('en-IN').format(squads[team.id].reduce((s, p) => s + (p.sold_price_byc || 0), 0))}
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
